import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
// library components
import { IconButton, Dialog, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import Alert from '@material-ui/lab/Alert';

// action
import { enqueueSuccessSnackbar } from 'redux/actions/notifierActions';
import { useDropzone } from 'react-dropzone';
import { uploadFile } from 'utils/uploadFile';
import { showAppOverlaySpinner } from 'redux/actions/facilitatorSessionActions';
import { useInternalApiClient } from 'hooks/useInternalApiClient';
import { getContractUploadGateError } from 'lib/getApiFieldErrors';
import Spinner from 'components/Spinner/Spinner';
import PillButton from 'components/PillButton';

const useStyles = makeStyles(theme => ({
  closeIcon: {
    position: 'relative',
    right: `-${theme.spacing(2)}px`,
    top: `-${theme.spacing(1)}px`,
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    height: 210,
    padding: 20,
    background: '#E1E1E1',
    borderRadius: 2,
    border: '1px solid #D6D6D6',
  },
  image: {
    height: 170,
  },
  uploadContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 170,
    border: '1px dashed #C3C3C3',
    cursor: 'pointer',
  },
  uploadContainerDrag: {
    border: `2px dashed #CFCFCF !important`,
    background: 'rgba(0,0,0,.1)',
  },
  icon: {
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontSize: 30,
  },
  text: {
    color: theme.palette.primary.main,
    fontSize: 11,
  },
  removeButton: {
    position: 'absolute',
    top: -5,
    right: -5,
    padding: 4,
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    background: '#f0f0f0',
    '&:hover': {
      background: '#f0f0f0',
    },
  },
  cancelIcon: {
    fontSize: 13,
  },
  blockersList: {
    margin: `${theme.spacing(1)}px 0 0`,
    paddingLeft: theme.spacing(3),
  },
  /**
   * Inline P&L-gate loading row. Spinner sits to the left of the copy so the
   * dialog does not lean on a trailing ellipsis to imply work in progress.
   */
  checkingStatus: {
    display: 'flex',
    alignItems: 'center',
  },
  checkingSpinner: {
    marginRight: theme.spacing(1),
  },
}));

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '50px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#D6D6D6',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: 'black',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const disabledStyle = {
  cursor: 'not-allowed',
  opacity: 0.55,
};

/**
 * Renders a Dialog to upload signed deal contracts.
 *
 * First upload (`create`) preflights Agents Dash P&L: blockers and API failures
 * are listed in the dialog (not a snackbar) and the dropzone stays disabled until
 * the gate is clear. A clear gate shows a success Alert above the dropzone.
 * Replace still allows an amended PDF even when P&L is incomplete; upload 409s
 * still list inline so a stale POST is visible.
 */
export default function UploadSignedContractDialog({
  handleClose,
  isOpen,
  closeDialog,
  dealId,
  actionType,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const apiClient = useInternalApiClient();
  const [gateLoading, setGateLoading] = useState(false);
  const [gateBlockers, setGateBlockers] = useState([]);
  const [gateUrl, setGateUrl] = useState(null);
  /**
   * True after the create-path gate GET has settled (success or failure).
   * Used so the success Alert does not flash on the first paint before the
   * effect sets `gateLoading`.
   */
  const [gateChecked, setGateChecked] = useState(false);
  /**
   * True when a drop reached the API and then failed. Distinguishes preflight
   * blockers (nothing left the browser) from a rolled-back Drive write.
   */
  const [uploadRolledBack, setUploadRolledBack] = useState(false);
  /**
   * Dialog `open` is mirrored in a ref so the exit animation can finish with
   * the last painted body. Resetting blockers/loading in a `!isOpen` effect
   * unmounts copy while MUI is still fading out, which shrinks the paper.
   */
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  /**
   * Clears P&L gate UI after the close transition. Skips if the dialog was
   * reopened before `onExited` fired (MUI can invoke that after a new open).
   */
  const resetGateStateAfterClose = () => {
    if (isOpenRef.current) {
      return;
    }
    setGateBlockers([]);
    setGateUrl(null);
    setGateLoading(false);
    setGateChecked(false);
    setUploadRolledBack(false);
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    // Replace must not be blocked on P&L — an amended PDF still has to land.
    if (actionType !== 'create' || !dealId) {
      setGateBlockers([]);
      setGateUrl(null);
      setGateLoading(false);
      setGateChecked(false);
      setUploadRolledBack(false);
      return undefined;
    }

    let cancelled = false;
    setGateLoading(true);
    setGateChecked(false);
    setUploadRolledBack(false);

    apiClient.deals
      .getProposalContractGate(dealId)
      .then(result => {
        if (cancelled) {
          return;
        }
        setGateBlockers(Array.isArray(result?.blockers) ? result.blockers : []);
        setGateUrl(result?.url ?? null);
        setGateChecked(true);
      })
      .catch(error => {
        if (cancelled) {
          return;
        }
        // HTTP 401 from Agents Dash is converted to 200 blockers server-side.
        // Remaining failures (5xx, network) still belong in this Alert, not a snackbar.
        const gateError = getContractUploadGateError(error);
        setGateBlockers(gateError.blockers);
        setGateUrl(gateError.url);
        setGateChecked(true);
      })
      .finally(() => {
        if (!cancelled) {
          setGateLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, actionType, dealId, apiClient]);

  const dropzoneDisabled = actionType === 'create' && (gateLoading || gateBlockers.length > 0);
  /**
   * Create-only: the gate GET returned 200 with an empty blocker list. Replace
   * skips the preflight, so it never shows this success row.
   */
  const showGateSuccess =
    actionType === 'create' && gateChecked && !gateLoading && gateBlockers.length === 0;

  const onDrop = async acceptedFiles => {
    if (dropzoneDisabled) {
      return;
    }

    dispatch(showAppOverlaySpinner(true));
    setUploadRolledBack(false);

    // Only true after the PDF is in Firebase Storage. The API then copies it
    // to Drive; a later 409 trashes Drive and deletes that Storage object.
    let storedOnFirebase = false;

    try {
      await uploadFile(acceptedFiles[0], `signed-contracts`, { dealId });
      storedOnFirebase = true;
      await apiClient.deals.uploadSignedContract(dealId, {
        storagePath: `signed-contracts/${acceptedFiles[0].name}`,
        action: actionType,
      });
      dispatch(enqueueSuccessSnackbar());
      closeDialog();
    } catch (error) {
      // Keep the dialog open and list blockers / API copy inline. A snackbar
      // covers the overlay and is easy to miss while this paper is still up.
      const gateError = getContractUploadGateError(error);
      setGateBlockers(gateError.blockers);
      setGateUrl(gateError.url);
      setUploadRolledBack(storedOnFirebase);
    } finally {
      dispatch(showAppOverlaySpinner(false));
    }
  };

  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept: ['application/pdf'],
    maxFiles: 1,
    noClick: true,
    disabled: dropzoneDisabled,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...(dropzoneDisabled ? disabledStyle : {}),
    }),
    [isDragAccept, isDragReject, dropzoneDisabled],
  );

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionProps={{ onExited: resetGateStateAfterClose }}
      >
        <DialogTitle disableTypography className="pb-0">
          <div className="d-flex justify-content-between">
            <Typography variant="h6">
              {actionType === 'replace' ? 'Replace Signed Contract' : 'Upload Signed Contract'}
            </Typography>
            <IconButton aria-label="close" onClick={handleClose} className={classes.closeIcon}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className="pb-4">
          <div>
            {actionType === 'replace' && (
              <Alert severity="warning" className="mb-3">
                A signed contract was previously uploaded. If you drop a new file here the old one
                will be moved to the trash in Google Drive.
              </Alert>
            )}

            {actionType === 'create' && gateLoading && (
              <Typography
                component="div"
                className={`${classes.checkingStatus} mb-3`}
                color="textSecondary"
              >
                <Spinner size={16} className={classes.checkingSpinner} />
                Checking Proposals dashboard
              </Typography>
            )}

            {gateBlockers.length > 0 && (
              <div className="mb-3">
                <Alert severity="error">
                  {uploadRolledBack
                    ? 'The signed contract was not saved. Resolve the following on the Proposals dashboard and try again:'
                    : 'This signed contract cannot be uploaded yet:'}
                  <ul className={classes.blockersList}>
                    {gateBlockers.map(blocker => (
                      <li key={blocker}>{blocker}</li>
                    ))}
                  </ul>
                </Alert>
                {gateUrl && (
                  <div className="mt-3">
                    <PillButton
                      type="externalUrl"
                      externalUrl={gateUrl}
                      text="Open Proposals dashboard"
                      EndIcon={ArrowForwardIcon}
                    />
                  </div>
                )}
              </div>
            )}

            {showGateSuccess && (
              <div className="mb-3">
                <Alert severity="success">
                  The P&amp;L on the Proposals dashboard is complete. You can upload a signed
                  contract.
                </Alert>
              </div>
            )}

            <div {...getRootProps({ style })}>
              <input {...getInputProps()} />
              {dropzoneDisabled && !gateLoading && (
                <Typography color="textSecondary">
                  Upload is blocked until the issues above are resolved
                </Typography>
              )}
              {!dropzoneDisabled && isDragReject && (
                <Typography color="error">Only PDFs are accepted</Typography>
              )}
              {!dropzoneDisabled && !isDragReject && (
                <Typography color="textSecondary">Drag 'n' drop PDF here</Typography>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

UploadSignedContractDialog.propTypes = {
  handleClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  closeDialog: PropTypes.func,
  dealId: PropTypes.string,
  actionType: PropTypes.oneOf(['create', 'replace']),
};
